import { ApplicationInsights } from "@microsoft/applicationinsights-web";

function hasAnalyticsConsent() {
  try {
    return localStorage.getItem("cookieConsent") === "acceptedAll";
  } catch {
    return false;
  }
}

// REACT_APP_APPINSIGHTS_KEY holds the connection string; a bare key still works until the GitHub variables are switched.
const appInsightsSetting = process.env.REACT_APP_APPINSIGHTS_KEY ?? "";

// ai_user / ai_session cookies only after consent; telemetry is sent either way.
const appInsights = new ApplicationInsights({
  config: {
    ...(appInsightsSetting.includes("InstrumentationKey=")
      ? { connectionString: appInsightsSetting }
      : { instrumentationKey: appInsightsSetting }),
    enableAutoRouteTracking: true,
    disableCookiesUsage: !hasAnalyticsConsent(),
  },
});

// Query strings can carry personal data (e.g. ?email=…&dateOfBirth=… on the email lander, sign-in tokens), so only
// campaign parameters are kept on any URL sent to App Insights.
function stripPersonalQuery(url: unknown) {
  if (typeof url !== "string" || !url.includes("?")) return url;
  const [base, rest] = [url.slice(0, url.indexOf("?")), url.slice(url.indexOf("?") + 1)];
  const query = new URLSearchParams(rest.split("#")[0]);
  const kept = new URLSearchParams();
  query.forEach((value, key) => {
    if (/^(utm|vt)_/i.test(key)) kept.append(key, value);
  });
  return kept.toString() ? `${base}?${kept.toString()}` : base;
}

appInsights.loadAppInsights();
appInsights.addTelemetryInitializer((item) => {
  const data = item.baseData as Record<string, unknown> | undefined;
  if (!data) return;
  ["uri", "refUri", "target", "name", "data"].forEach((field) => {
    if (field in data) data[field] = stripPersonalQuery(data[field]);
  });
});
appInsights.trackPageView();

function applyCookieConsent(granted: boolean) {
  const cookieManager = appInsights.getCookieMgr();
  cookieManager.setEnabled(granted);
  if (!granted) {
    cookieManager.del("ai_user");
    cookieManager.del("ai_session");
  }
}

// CookieConsent announces the choice through setLocalStorageWithEvent.
window.addEventListener("local-storage", (event) => {
  const { key, value } = (event as CustomEvent<{ key: string; value: string }>).detail ?? {};
  if (key === "cookieConsent") applyCookieConsent(value === "acceptedAll");
});

export default appInsights;
